import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

// Pages
import Unsubscribe from "./unsubscribe";
import Update from "./update";

// Components
import ErrorContainer from "../components/errorContainer";
import InputField from "../components/inputField";
import InputRadio from "../components/inputRadio";
import Button from "../components/button";

// Handlers
import { ApiHandler, customEventHandler } from "../services/handlers";

// Styles
import {
  MainWrapper,
  MainContainer,
  IconWrapper,
  Heading2,
  Heading3,
  Paragraph,
  Label,
  MainForm,
  MessageIcon,
  UnsubscribeText
} from "./main.style";

import { emailFrequencyIcon } from "../assets/cdn";

function Main({ data, authCode, executedBy, customerEmail }) {
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const [formValues, setFormValues] = useState({ radio: "2x per week" });

  useEffect(() => {
    if (data?.isSubscribed === false && data?.clientellingOptin === true) {
      setFormValues({
        email: customerEmail,
        frequency: "Out of newsletter",
        isSubscribed: data.isSubscribed,
        clientellingOptin: data.clientellingOptin,
        radio: "Out of newsletter",
      });
    } else {
      setFormValues(data);
    }
  }, [data, customerEmail]);

  // Translations data array
  const TransData = [
    {
      label: t("epc_radio_data.0.label"),
      value: t("epc_radio_data.0.value"),
      subLabel: t("epc_radio_data.0.subLabel"),
    },
    {
      label: t("epc_radio_data.1.label"),
      value: t("epc_radio_data.1.value"),
      subLabel: t("epc_radio_data.1.subLabel"),
    },
    {
      label: t("epc_radio_data.2.label"),
      value: t("epc_radio_data.2.value"),
      subLabel: t("epc_radio_data.2.subLabel"),
    },
    {
      label: t("epc_radio_data.3.label"),
      value: t("epc_radio_data.3.value"),
      subLabel: t("epc_radio_data.3.subLabel"),
    },
  ];

  // Input change handler
  const handleChange = (e) => {
    const { value, name } = e.target;
    setFormValues({
      ...formValues,
      frequency: value,
      [name]: value,
    });
  };

  // Subscription handler
  const handleSubscribe = async (e, radio) => {
    e.preventDefault();
    const valuesApi = {
      code: authCode,
      email: customerEmail,
      isNewsletterSubscribed:
        formValues?.radio === "Out of newsletter" ? false : true,
      newsletterFrequency:
        formValues?.frequency === "Out of newsletter"
          ? null
          : formValues?.radio || formValues?.frequency,
      clientellingOptin: true,
      executedBy: executedBy ? executedBy : null,
    };

    const labelsArray = [
      {
        value: "2x per week",
        label: "Twice_Per_Week",
      },
      {
        value: "1x per week",
        label: "Once_Per_Week",
      },
      {
        value: "1x per month",
        label: "Once_Per_Month",
      },
      {
        value: "Out of newsletter",
        label: "Out_Of_Newsletter",
      },
    ];

    // Convert the selected option for Google Analytics
    // Example: '1x per week' to 'Once_Per_Week'
    let convertedChoise = labelsArray.find(
      (x) => x.value === formValues.frequency
    )?.label;

    setLoading(true);
    await ApiHandler("update", valuesApi)
      .then((res) => {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        window.dataLayer.push({
          event: "GAevent",
          eventCategory: "PreferenceC_Interactions",
          eventAction: "Changed perference",
          eventLabel: convertedChoise,
        });

        setTimeout(() => {
          setLoading(false);
          setFormStep(2);
        }, 100);
        customEventHandler("EPC-Subscription");
      })
      .catch(() => {
        setError(true);
        setLoading(true);
      });
  };

  // Unsubscription handler
  const handleUnsubscribe = async () => {
    const valuesApi = {
      code: authCode,
      email: customerEmail,
      isNewsletterSubscribed: false,
      frequency: null,
      clientellingOptin: false,
      executedBy: executedBy ? executedBy : null,
    };

    setLoading(true);
    await ApiHandler("update", valuesApi)
      .then((res) => {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        window.dataLayer.push({
          event: "GAevent",
          eventCategory: "PreferenceC_Interactions",
          eventAction: "Unsubscribe",
        });

        setTimeout(() => {
          setLoading(false);
          setFormStep(1);
        }, 100);

        customEventHandler("EPC-Unsubscription");
      })
      .catch(() => {
        setError(true);
        setLoading(true);
      });
  };

  const reset = () => {
    setError(false);
    setLoading(false);
  };

  return (
    <>
      {error && (
        <ErrorContainer
          label={t("epc_error_message_btnLabel")}
          handleClick={reset}
        />
      )}
      <MainWrapper>
        {formStep === 0 && (
          <MainContainer>
            <IconWrapper>
              <MessageIcon url={emailFrequencyIcon} alt="emailFrequencyIcon" />
            </IconWrapper>
            <Heading2>{t("epc_main_heading2")}</Heading2>
            <Paragraph>{t("epc_main_paragraph1")}</Paragraph>

            <MainForm onSubmit={handleSubscribe} noValidate>
              <InputField
                id="email"
                type="email"
                name="email"
                placeHolder={customerEmail}
                onChange={() => handleChange}
                value={formValues?.email}
              />

              <Heading3>{t("epc_main_heading3")}</Heading3>
              <Paragraph>{t("epc_main_paragraph2")}</Paragraph>

              {TransData.map((item, index) => {
                return (
                  <InputRadio
                    key={index}
                    id={index}
                    label={item.label}
                    value={index}
                    iconType={index}
                    subLabel={item.subLabel}
                    onChange={handleChange}
                    isChecked={formValues?.frequency}
                  />
                );
              })}

              <Button
                isLoading={isLoading}
                label={t("epc_btn_label")}
                type="submit"
              />

              <Label htmlFor="button">
                {t("epc_main_paragraph3_part1")}
                &nbsp;
                <u onClick={handleUnsubscribe}>
                  {parse(t("epc_main_paragraph3_part2"))}
                </u>
                &nbsp;
                {t("epc_main_paragraph3_part3")}
              </Label>
            </MainForm>
          </MainContainer>
        )}
        {formStep === 1 && (
          <Unsubscribe data={formValues} customerEmail={customerEmail} />
        )}
        {formStep === 2 && (
          <Update data={formValues} customerEmail={customerEmail} />
        )}
        
      </MainWrapper>
      <UnsubscribeText>{t('epc_unsubscribe_paragraph')}</UnsubscribeText>
    </>
  );
}

export default Main;
